import AirDatepicker, {
  type AirDatepickerLocale,
  type AirDatepickerOptions,
  type AirDatepickerPosition,
} from 'air-datepicker';
import localeEn from 'air-datepicker/locale/en';
import localeId from 'air-datepicker/locale/id';
import localeKo from 'air-datepicker/locale/ko';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/shared/components/ui/Button';
import ContainerCard from '@/shared/components/ui/ContainerCard/ContainerCard';
import Filtering from '@/shared/components/ui/Filtering/Filtering';
import GeneralInput from '@/shared/components/ui/GeneralInput/GeneralInput';
import { formatDateTime } from '@/shared/utils/date';
import 'air-datepicker/air-datepicker.css';

import IndoorAirStatictic from '@/domains/statistic/components/IndoorAirStatictic';
import OutdoorAirStatictic from '@/domains/statistic/components/OutdoorAirStatictic';
import useAirStats from '@/domains/statistic/hooks/useAirStats';

const buildParams = (startDate: string, endDate: string, interval: number, search: string) => {
  const parts: string[] = [];
  if (startDate) parts.push(`start_time=${encodeURIComponent(startDate.replace(' ', 'T'))}`);
  if (endDate) parts.push(`end_time=${encodeURIComponent(endDate.replace(' ', 'T'))}`);
  parts.push(`interval=${interval}`);
  if (search) parts.push(`search=${encodeURIComponent(search)}`);
  return parts.join('&');
};

const EnvironmentStatistic = () => {
  const { i18n } = useTranslation();
  const [_isResetClicked, setIsResetClicked] = useState(false);
  const today = new Date();
  const midnight = new Date(new Date().setHours(0, 0, 0, 0));
  const startDateRef = useRef<Date>(midnight);
  const endDateRef = useRef<Date>(today);
  const [activeButton, setActiveButton] = useState('5분');
  const [interval, setInterval] = useState(5);
  const [dateTime, setDateTime] = useState({
    start_date: formatDateTime(midnight),
    end_date: formatDateTime(today),
  });
  const [search, setSearch] = useState('');
  const [queryParams, setQueryParams] = useState(
    buildParams(formatDateTime(midnight), formatDateTime(today), 5, '')
  );

  const handleButtonClick = (label: string, newInterval: number) => {
    setActiveButton(label);
    setInterval(newInterval);
    setQueryParams(buildParams(dateTime.start_date, dateTime.end_date, newInterval, search));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setDateTime((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    let locale: AirDatepickerLocale;
    if (i18n.language === 'eng') {
      locale = localeEn;
    } else if (i18n.language === 'ind') {
      locale = localeId;
    } else {
      locale = localeKo;
    }
    const today = new Date();
    const midnight = new Date(new Date().setHours(0, 0, 0, 0));

    const optionsDate: AirDatepickerOptions = {
      autoClose: true,
      locale,
      position: 'bottom center' as unknown as AirDatepickerPosition,
      selectedDates: [midnight],
      timepicker: true,
      timeFormat: 'HH:mm',
      dateFormat: 'yyyy-MM-dd',
      onBeforeSelect: ({ date }) => {
        if (!date) return true;
        const d = date as Date;
        return d <= endDateRef.current;
      },
      onSelect: ({ date, formattedDate }) => {
        if (!date || !formattedDate) return;
        startDateRef.current = date as Date;
        setDateTime((prev) => ({ ...prev, start_date: formattedDate as string }));
      },
    };

    const optionsDateSecond: AirDatepickerOptions = {
      autoClose: true,
      locale,
      position: 'bottom center' as unknown as AirDatepickerPosition,
      timepicker: true,
      timeFormat: 'HH:mm',
      dateFormat: 'yyyy-MM-dd',
      selectedDates: [today],
      onBeforeSelect: ({ date }) => {
        if (!date) return true;
        const d = date as Date;
        return d >= startDateRef.current;
      },
      onSelect: ({ date, formattedDate }) => {
        if (!date || !formattedDate) return;
        endDateRef.current = date as Date;
        setDateTime((prev) => ({ ...prev, end_date: formattedDate as string }));
      },
    };

    const datepicker1 = new AirDatepicker('[name="start_date"]', optionsDate);
    const datepicker2 = new AirDatepicker('[name="end_date"]', optionsDateSecond);
    setIsResetClicked(false);
    return () => {
      datepicker1.destroy();
      datepicker2.destroy();
    };
  }, [i18n.language]);

  const [activeTab, setActiveTab] = useState(0);

  const { metrics: outdoorMetrics } = useAirStats({
    params: queryParams,
    sensorType: 'AIR_TP_OUT',
  });
  const { metrics: indoorMetrics } = useAirStats({ params: queryParams, sensorType: 'AIR_TP_IN' });

  const handleReset = () => {
    setIsResetClicked(true);
    const resetStart = formatDateTime(midnight);
    const resetEnd = formatDateTime(today);
    startDateRef.current = midnight;
    endDateRef.current = today;
    setDateTime({ start_date: resetStart, end_date: resetEnd });
    setActiveButton('5분');
    setInterval(5);
    setSearch('');
    setQueryParams(buildParams(resetStart, resetEnd, 5, ''));
  };

  const handleSearch = useCallback(
    (inputVal = '') => {
      setSearch(inputVal);
      setQueryParams(buildParams(dateTime.start_date, dateTime.end_date, interval, inputVal));
    },
    [dateTime, interval]
  );

  const tabs = [
    {
      label: '옥외형 대기질',
      content: <OutdoorAirStatictic metrics={outdoorMetrics} queryParams={queryParams} />,
    },
    {
      label: '실내형 공기질',
      content: <IndoorAirStatictic metrics={indoorMetrics} queryParams={queryParams} />,
    },
  ];

  return (
    <section className="wrap">
      <div className="header-title">
        <h3>통계</h3>
        <h3>&gt;</h3>
        <h3>환경 통계</h3>
      </div>

      <ContainerCard>
        <Filtering
          placeholder="사이트명"
          customWidthInput=" flex xl:flex-[0.8] lg:flex-1 md:flex-1 "
          showLabel={false}
          onSearch={handleSearch}
          onReset={handleReset}
          disableFiltering={true}
          customWidthSelect="w-full flex flex-1 basis-[700px]"
        >
          <div className="flex w-fit gap-2 flex-row items-center">
            <span className="text-sm text-gray-500 whitespace-nowrap">간격</span>
            {(
              [
                ['5분', 5],
                ['15분', 15],
                ['30분', 30],
                ['60분', 60],
              ] as const
            ).map(([label, val]) => (
              <Button
                key={label}
                customButton={`button filtering cursor-pointer hover:opacity-80 ${activeButton === label ? 'active' : ''}`}
                label={label}
                onClick={() => handleButtonClick(label, val)}
              />
            ))}
          </div>
          <div className="flex flex-row gap-2 items-center w-full">
            <GeneralInput
              isDob={true}
              type="text"
              name="start_date"
              value={dateTime.start_date}
              onChange={handleInputChange}
            />
            <span>-</span>
            <GeneralInput
              isDob={true}
              type="text"
              name="end_date"
              value={dateTime.end_date}
              onChange={handleInputChange}
            />
          </div>
        </Filtering>
      </ContainerCard>

      <div className="w-full">
        <div className="flex flex-row gap-4 bg-[#E6E6E6] dark:bg-[#1a2535] rounded-tl-lg rounded-tr-lg">
          {tabs.map((tab, index) => (
            <button
              type="button"
              key={tab.label}
              className={`w-fit py-2 text-center cursor-pointer hover:opacity-80 ${
                activeTab === index
                  ? 'bg-[#FEFEFE] dark:bg-[#1e3043] text-[#135A78] dark:text-[#9cc8dc] font-bold px-4 rounded-tl-lg rounded-tr-lg border-t border-l border-r border-[#E6E6E6] dark:border-[#2b465f] text-[14px]'
                  : 'border-transparent text-[#6A6A6A] dark:text-[#808890] px-4 text-[14px]'
              }`}
              onClick={() => setActiveTab(index)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="bg-[#fff] dark:bg-[#1a2030] p-[20px] border-r border-l border-b border-[#E6E6E6] dark:border-[#2b465f] rounded-bl-[10px] rounded-br-[10px]">
          {tabs[activeTab]?.content}
        </div>
      </div>
    </section>
  );
};

export default EnvironmentStatistic;
